import React from 'react';
import {
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    MAPPING_STATUS_COLOR,
    MappingSourceRow,
    RUN_STATUS_COLOR,
    describeLastRun,
    describeRunKind,
    runNeedsAttention,
} from './source-rows';
import {describeMappingStatus, describeMappingStatusDetail} from './mapping-status-text';

interface ComponentSourceTableProps {
    rows: MappingSourceRow[];
    selectedSourceName: string | null;
    onSelect: (sourceName: string) => void;
}

/**
 * Every report source and where this component version stands against it. Choosing a row is how
 * the run controls below it are pointed at a source, so the list of what exists and the way to add
 * to it are the same control.
 *
 * <p>A row leads with the derived mapping status, not with how the last run ended. A run that
 * completed is a fact about the run: a completed test of a hundred reports leaves a source of
 * fifty thousand almost entirely unmapped, and the row has to say so. The run's own state is shown
 * beside the date only when it is not the steady completed case.</p>
 */
const ComponentSourceTable: React.FC<ComponentSourceTableProps> = (
    {rows, selectedSourceName, onSelect}: ComponentSourceTableProps,
) => {
    if (rows.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No report sources are available to map against.
            </Typography>
        );
    }

    return (
        <TableContainer sx={{maxHeight: 260}}>
            <Table size="small" stickyHeader role="grid" aria-label="Report sources">
                <TableHead>
                    <TableRow>
                        <TableCell>Source</TableCell>
                        <TableCell align="right">Reports</TableCell>
                        <TableCell>Mapping status</TableCell>
                        <TableCell>Last run</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map(row => (
                        <TableRow
                            key={row.sourceName}
                            hover
                            // The row is the control that picks a source, so it has to answer to a
                            // keyboard as well as a pointer: MUI's `selected` is presentation only.
                            // aria-selected on a row is meaningful because the table declares
                            // itself a grid below.
                            tabIndex={0}
                            aria-selected={row.sourceName === selectedSourceName}
                            selected={row.sourceName === selectedSourceName}
                            onClick={(): void => onSelect(row.sourceName)}
                            onKeyDown={(event: React.KeyboardEvent): void => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    onSelect(row.sourceName);
                                }
                            }}
                            sx={{cursor: 'pointer'}}
                            data-testid={`mapping-source-row-${row.sourceName}`}
                        >
                            <TableCell>{row.sourceName}</TableCell>
                            <TableCell align="right">
                                {row.reportCount != null ? (
                                    row.reportCount.toLocaleString()
                                ) : (
                                    <Tooltip title="This source is no longer listed. Its records stay readable, but it cannot be run against again.">
                                        <Typography variant="body2" color="text.secondary" component="span">
                                            not listed
                                        </Typography>
                                    </Tooltip>
                                )}
                            </TableCell>
                            <TableCell>
                                {row.status ? (
                                    <Tooltip title={describeMappingStatusDetail(row.status)}>
                                        <Chip
                                            size="small"
                                            label={describeMappingStatus(row.status)}
                                            color={MAPPING_STATUS_COLOR[row.status.status] ?? 'default'}
                                        />
                                    </Tooltip>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">Not mapped</Typography>
                                )}
                            </TableCell>
                            <TableCell>
                                <Typography
                                    variant="body2"
                                    component="span"
                                    color={row.state?.lastRunAt ? 'text.primary' : 'text.secondary'}
                                >
                                    {describeLastRun(row.state)}
                                </Typography>
                                {runNeedsAttention(row.state) && row.state?.runStatus && (
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        sx={{ml: 1}}
                                        label={row.state.runStatus}
                                        color={RUN_STATUS_COLOR[row.state.runStatus] ?? 'default'}
                                    />
                                )}
                                {describeRunKind(row.state) && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {describeRunKind(row.state)}
                                    </Typography>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default ComponentSourceTable;
